// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { ContactId } from './ContactId';
import { Contact } from './Contact';
import { TeamId } from './TeamId';
import { Document } from './Document';
import { StructuredAddress } from './StructuredAddress';
import { EmailAddress } from './EmailAddress';
import { PhoneNumber } from './PhoneNumber';
import { AuditMetadata } from './AuditMetadata';

import type { ContactIdModelType } from './ContactId';
import type { ContactModelType } from './Contact';
import type { TeamIdModelType } from './TeamId';
import type { DocumentModelType } from './Document';
import type { StructuredAddressModelType } from './StructuredAddress';
import type { EmailAddressModelType } from 'common/models/flow/EmailAddress';
import type { PhoneNumberModelType } from 'common/models/flow/PhoneNumber';
import type { AuditMetadataModelType } from './AuditMetadata';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';

export type TransporterTypeType = 'OWNED_FLEET' | 'OUTSOURCED_FLEET';
export type TransporterScopeType =
    | 'LOCAL'
    | 'REGIONAL'
    | 'NATIONAL'
    | 'INTERNATIONAL';
export type TransporterServiceType =
    | 'EXPRESS_DISTRIBUTION'
    | 'FREIGHT_FORWARDING'
    | 'WAREHOUSING_SOLUTIONS'
    | 'COLDCHAIN_SOLUTIONS'
    | 'COURIER';

export type MasterTransporterModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    isActive: boolean,
    contactId: ?ContactIdModelType,
    contact: ?ContactModelType,
    teams: Array<TeamIdModelType>,
    type: TransporterTypeType,
    scope: TransporterScopeType,
    services: Array<TransporterServiceType>,
    color: string,
    registeredName: string,
    tagline: ?string,
    address: StructuredAddressModelType,
    logo: ?string,
    emailAddress: ?EmailAddressModelType,
    additionalEmailAddresses: ?Array<EmailAddressModelType>,
    phoneNumber: ?PhoneNumberModelType,
    additionalPhoneNumbers: Array<PhoneNumberModelType>,
    documents: Array<DocumentModelType>,
    customProperties: { [key: string]: string },
    auditMetadata: AuditMetadataModelType,
    version: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterTransporter extends ImmutableModel {
    static fromJS(json: MasterTransporterModelType): MasterTransporter {
        const state: Object = Object.assign({}, json);
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.services = Immutable.List(state.services);
        state.address = StructuredAddress.fromJS(state.address);
        state.emailAddress = state.emailAddress
            ? EmailAddress.fromJS(state.emailAddress)
            : state.emailAddress;
        state.additionalEmailAddresses = state.additionalEmailAddresses
            ? Immutable.List(state.additionalEmailAddresses).map(item =>
                  EmailAddress.fromJS(item)
              )
            : state.additionalEmailAddresses;
        state.phoneNumber = state.phoneNumber
            ? PhoneNumber.fromJS(state.phoneNumber)
            : state.phoneNumber;
        state.additionalPhoneNumbers = Immutable.List(
            state.additionalPhoneNumbers
        ).map(item => PhoneNumber.fromJS(item));
        state.documents = Immutable.List(state.documents).map(item =>
            Document.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        return new this(Immutable.Map(state));
    }

    toJS(): MasterTransporterModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            isActive: this.isActive,
            contactId: this.contactId ? this.contactId.toJS() : this.contactId,
            contact: this.contact ? this.contact.toJS() : this.contact,
            teams: this.teams.toArray().map(item => item.toJS()),
            type: this.type,
            scope: this.scope,
            services: this.services.toArray(),
            color: this.color,
            registeredName: this.registeredName,
            tagline: this.tagline,
            address: this.address.toJS(),
            logo: this.logo,
            emailAddress: this.emailAddress
                ? this.emailAddress.toJS()
                : this.emailAddress,
            additionalEmailAddresses: this.additionalEmailAddresses
                ? this.additionalEmailAddresses
                      .toArray()
                      .map(item => item.toJS())
                : this.additionalEmailAddresses,
            phoneNumber: this.phoneNumber
                ? this.phoneNumber.toJS()
                : this.phoneNumber,
            additionalPhoneNumbers: this.additionalPhoneNumbers
                .toArray()
                .map(item => item.toJS()),
            documents: this.documents.toArray().map(item => item.toJS()),
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata.toJS(),
            version: this.version
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get isActive(): boolean {
        return this._state.get('isActive');
    }

    setIsActive(isActive: boolean): this {
        return this.clone(this._state.set('isActive', isActive));
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    setContactId(contactId: ?ContactId): this {
        return this.clone(this._state.set('contactId', contactId));
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    setContact(contact: ?Contact): this {
        return this.clone(this._state.set('contact', contact));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get type(): TransporterTypeType {
        return this._state.get('type');
    }

    setType(type: TransporterTypeType): this {
        return this.clone(this._state.set('type', type));
    }

    get scope(): TransporterScopeType {
        return this._state.get('scope');
    }

    setScope(scope: TransporterScopeType): this {
        return this.clone(this._state.set('scope', scope));
    }

    get services(): Immutable.List<TransporterServiceType> {
        return this._state.get('services');
    }

    setServices(services: Immutable.List<TransporterServiceType>): this {
        return this.clone(this._state.set('services', services));
    }

    get color(): string {
        return this._state.get('color');
    }

    setColor(color: string): this {
        return this.clone(this._state.set('color', color));
    }

    get registeredName(): string {
        return this._state.get('registeredName');
    }

    setRegisteredName(registeredName: string): this {
        return this.clone(this._state.set('registeredName', registeredName));
    }

    get tagline(): ?string {
        return this._state.get('tagline');
    }

    setTagline(tagline: ?string): this {
        return this.clone(this._state.set('tagline', tagline));
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get logo(): ?string {
        return this._state.get('logo');
    }

    setLogo(logo: ?string): this {
        return this.clone(this._state.set('logo', logo));
    }

    get emailAddress(): ?EmailAddress {
        return this._state.get('emailAddress');
    }

    setEmailAddress(emailAddress: ?EmailAddress): this {
        return this.clone(this._state.set('emailAddress', emailAddress));
    }

    get additionalEmailAddresses(): ?Immutable.List<EmailAddress> {
        return this._state.get('additionalEmailAddresses');
    }

    setAdditionalEmailAddresses(
        additionalEmailAddresses: ?Immutable.List<EmailAddress>
    ): this {
        return this.clone(
            this._state.set(
                'additionalEmailAddresses',
                additionalEmailAddresses
            )
        );
    }

    get phoneNumber(): ?PhoneNumber {
        return this._state.get('phoneNumber');
    }

    setPhoneNumber(phoneNumber: ?PhoneNumber): this {
        return this.clone(this._state.set('phoneNumber', phoneNumber));
    }

    get additionalPhoneNumbers(): Immutable.List<PhoneNumber> {
        return this._state.get('additionalPhoneNumbers');
    }

    setAdditionalPhoneNumbers(
        additionalPhoneNumbers: Immutable.List<PhoneNumber>
    ): this {
        return this.clone(
            this._state.set('additionalPhoneNumbers', additionalPhoneNumbers)
        );
    }

    get documents(): Immutable.List<Document> {
        return this._state.get('documents');
    }

    setDocuments(documents: Immutable.List<Document>): this {
        return this.clone(this._state.set('documents', documents));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }
}
